import { useCallback, useEffect, useState } from "react";
import { Clock3, ImageUp, Mail, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Settings() {
  const [dayStart, setDayStart] = useState("04:00");
  const [windows, setWindows] = useState([]);
  const [staffTypes, setStaffTypes] = useState([]);
  const [newType, setNewType] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const { restaurant, saveSession } = useAuth();
  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);

  const refreshSession = async () => {
    const { data } = await api.get("/auth/me");
    saveSession(data);
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/settings/logo", fd);
      await refreshSession();
      toast.success("Business logo updated");
    } catch (err) {
      toast.error(formatApiError(err, "Logo upload failed"));
    } finally {
      setLogoBusy(false);
    }
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    try {
      await api.delete("/settings/logo");
      await refreshSession();
      toast.success("Logo removed");
    } catch (err) {
      toast.error(formatApiError(err, "Could not remove logo"));
    } finally {
      setLogoBusy(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setDayStart(data.business_day_start);
      setWindows(data.windows);
      setStaffTypes(data.staff_types || []);
      setLoaded(true);
    } catch (err) {
      toast.error(formatApiError(err, "Could not load settings"));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateWindow = (i, k, v) => {
    const next = [...windows];
    next[i] = { ...next[i], [k]: v };
    setWindows(next);
  };

  const addType = async () => {
    if (!newType.trim()) return;
    try {
      await api.post("/settings/staff-types", { name: newType.trim() });
      toast.success(`"${newType.trim()}" added`);
      setNewType("");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not add staff type"));
    }
  };

  const removeType = async (t) => {
    try {
      await api.delete(`/settings/staff-types/${t.id}`);
      toast.success(`"${t.name}" removed`);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not remove staff type"));
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", { business_day_start: dayStart, windows });
      toast.success("Settings saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not save settings"));
    } finally {
      setBusy(false);
    }
  };

  const changeEmail = async () => {
    setEmailBusy(true);
    try {
      await api.post("/auth/change-email", { new_email: newEmail.trim(), current_password: emailPassword });
      const { data } = await api.get("/auth/me");
      saveSession(data);
      toast.success("Sign-in email updated");
      setNewEmail("");
      setEmailPassword("");
    } catch (err) {
      toast.error(formatApiError(err, "Could not update email"));
    } finally {
      setEmailBusy(false);
    }
  };

  if (!loaded) {
    return <div className="min-h-[60vh] grid place-items-center text-terra-600 animate-pulse font-display">Loading settings…</div>;
  }

  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-8 py-8" data-testid="settings-page">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1A1D20]">Shift Settings</h1>
      <p className="text-sm text-slate-500 mt-1">Time windows, stations context, and your operational day.</p>

      <OperationalDaySection dayStart={dayStart} onChange={setDayStart} />

      <StaffTypesSection staffTypes={staffTypes} newType={newType} setNewType={setNewType} onAdd={addType} onRemove={removeType} />

      <WindowsSection windows={windows} setWindows={setWindows} onUpdate={updateWindow} />

      <section className="mt-6 p-5 sm:p-6 rounded-2xl bg-white border border-[#E4DDD3]" data-testid="business-logo-section">
        <h2 className="text-base md:text-lg font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-2">
          <ImageUp className="w-4 h-4 text-terra-600" /> Business Logo
        </h2>
        <p className="text-sm text-slate-500 mt-2">Your logo appears in the app header for everyone once they sign in.</p>
        <div className="mt-4 flex items-center gap-4 flex-wrap">
          {restaurant?.logo_path ? (
            <img
              src={`/api/files/${restaurant.logo_path}`}
              alt="Business logo"
              className="h-16 w-auto max-w-[220px] object-contain rounded-xl border border-[#E4DDD3] bg-[#FAF6F0] p-2"
              data-testid="business-logo-preview"
            />
          ) : (
            <div className="h-16 w-16 rounded-xl border border-dashed border-[#E4DDD3] bg-[#FAF6F0] grid place-items-center text-slate-400" data-testid="business-logo-empty">
              <ImageUp className="w-5 h-5" />
            </div>
          )}
          <div className="flex items-center gap-2">
            <label
              className={`h-11 px-5 rounded-xl bg-terra-500 text-white text-sm font-bold hover:bg-terra-600 transition-colors flex items-center gap-2 cursor-pointer ${logoBusy ? "opacity-50 pointer-events-none" : ""}`}
              data-testid="business-logo-upload-button"
            >
              {logoBusy ? "Uploading…" : restaurant?.logo_path ? "Replace Logo" : "Upload Logo"}
              <input
                type="file"
                accept="image/*"
                onChange={uploadLogo}
                className="hidden"
                data-testid="business-logo-input"
              />
            </label>
            {restaurant?.logo_path && (
              <button
                onClick={removeLogo}
                disabled={logoBusy}
                data-testid="business-logo-remove-button"
                className="h-11 px-4 rounded-xl border border-rose-300/40 text-rose-600 text-sm font-medium hover:bg-rose-50 disabled:opacity-50 transition-colors"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="mt-6 p-5 sm:p-6 rounded-2xl bg-white border border-[#E4DDD3]" data-testid="change-email-section">
        <h2 className="text-base md:text-lg font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-2">
          <Mail className="w-4 h-4 text-terra-600" /> Sign-in Email
        </h2>
        <p className="text-sm text-slate-500 mt-2">Change the email you use to sign in. Your current password is required to confirm.</p>
        <div className="mt-4 space-y-3 max-w-sm">
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="New email address"
            data-testid="change-email-input"
            className="w-full h-12 px-4 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] placeholder-slate-400 focus:outline-none focus:border-terra-400/60"
          />
          <input
            type="password"
            value={emailPassword}
            onChange={(e) => setEmailPassword(e.target.value)}
            placeholder="Current password"
            data-testid="change-email-password-input"
            className="w-full h-12 px-4 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] placeholder-slate-400 focus:outline-none focus:border-terra-400/60"
          />
          <button
            onClick={changeEmail}
            disabled={emailBusy || !newEmail.trim() || !emailPassword}
            data-testid="change-email-save-button"
            className="h-11 px-5 rounded-xl bg-terra-500 text-white text-sm font-bold hover:bg-terra-600 disabled:opacity-50 transition-colors"
          >
            {emailBusy ? "Updating…" : "Update Email"}
          </button>
        </div>
      </section>

      <button
        onClick={save}
        disabled={busy}
        data-testid="settings-save-button"
        className="mt-6 h-12 px-6 rounded-xl bg-terra-500 text-white font-display font-bold flex items-center gap-2 hover:bg-terra-600 disabled:opacity-50 transition-colors"
      >
        <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save Settings"}
      </button>
    </main>
  );
}

function OperationalDaySection({ dayStart, onChange }) {
  return (
    <section className="mt-8 p-5 sm:p-6 rounded-2xl bg-white border border-[#E4DDD3]">
      <h2 className="text-base md:text-lg font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-2">
        <Clock3 className="w-4 h-4 text-terra-600" /> Operational Day
      </h2>
      <p className="text-sm text-slate-500 mt-2">
        Work done before this time counts toward the previous business day — so a 1:30 AM close logs under last night, not today.
      </p>
      <div className="mt-4 flex items-center gap-3">
        <label className="text-sm text-slate-600">Business day starts at</label>
        <input
          type="time"
          value={dayStart}
          onChange={(e) => onChange(e.target.value)}
          data-testid="settings-business-day-start-input"
          className="h-12 px-4 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] font-mono focus:outline-none focus:border-terra-400/60"
        />
      </div>
    </section>
  );
}

function StaffTypesSection({ staffTypes, newType, setNewType, onAdd, onRemove }) {
  return (
    <section className="mt-6 p-5 sm:p-6 rounded-2xl bg-white border border-[#E4DDD3]" data-testid="staff-types-section">
      <h2 className="text-base md:text-lg font-semibold text-slate-700 uppercase tracking-wider">Staff Types</h2>
      <p className="text-sm text-slate-500 mt-1">
        The roles staff pick when joining and that templates are built for — e.g. Server, Technician, Custodian.
      </p>
      <div className="mt-4 flex gap-2 flex-wrap" data-testid="staff-types-list">
        {staffTypes.length === 0 && <span className="text-sm text-slate-500">No staff types yet.</span>}
        {staffTypes.map((t) => (
          <span
            key={t.id}
            data-testid={`staff-type-chip-${t.id}`}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#E4DDD3] text-sm text-slate-700"
          >
            {t.name}
            <button
              onClick={() => onRemove(t)}
              data-testid={`delete-staff-type-button-${t.id}`}
              className="text-slate-500 hover:text-rose-600 transition-colors"
              title="Remove"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </span>
        ))}
      </div>
      <div className="mt-4 flex gap-2">
        <input
          value={newType}
          onChange={(e) => setNewType(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), onAdd())}
          placeholder='e.g. "Grill Cook"'
          data-testid="add-staff-type-input"
          className="h-11 px-4 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] placeholder-slate-400 focus:outline-none focus:border-terra-400/60 flex-1 max-w-xs"
        />
        <button
          onClick={onAdd}
          disabled={!newType.trim()}
          data-testid="add-staff-type-button"
          className="h-11 px-4 rounded-xl border border-terra-400/50 text-terra-600 text-sm font-medium flex items-center gap-2 hover:bg-[#FDF0EB] disabled:opacity-40 transition-colors"
        >
          <Plus className="w-4 h-4" /> Add Staff Type
        </button>
      </div>
    </section>
  );
}

function WindowsSection({ windows, setWindows, onUpdate }) {
  return (
    <section className="mt-6 p-5 sm:p-6 rounded-2xl bg-white border border-[#E4DDD3]">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base md:text-lg font-semibold text-slate-700 uppercase tracking-wider">Shift Windows</h2>
          <p className="text-sm text-slate-500 mt-1">Tasks group into these windows. Overnight windows (e.g. 16:00–02:00) wrap past midnight.</p>
        </div>
        <button
          onClick={() => setWindows([...windows, { id: null, name: "", start: "10:00", end: "15:00" }])}
          data-testid="add-window-button"
          className="h-10 px-4 rounded-xl border border-terra-400/50 text-terra-600 text-sm font-medium flex items-center gap-2 hover:bg-[#FDF0EB] transition-colors"
        >
          <Plus className="w-4 h-4" /> Add Window
        </button>
      </div>

      <div className="mt-4 space-y-3">
        {windows.length === 0 && (
          <div className="text-sm text-slate-500" data-testid="no-windows-message">No windows defined — templates apply all day.</div>
        )}
        {windows.map((w, i) => (
          <div key={w.id || `new-${i}`} className="flex items-center gap-2 flex-wrap" data-testid={`window-row-${i}`}>
            <input
              value={w.name}
              onChange={(e) => onUpdate(i, "name", e.target.value)}
              placeholder='e.g. "Opening"'
              data-testid={`window-name-input-${i}`}
              className="h-11 px-3 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] placeholder-slate-400 focus:outline-none focus:border-terra-400/60 flex-1 min-w-[120px]"
            />
            <input
              type="time"
              value={w.start}
              onChange={(e) => onUpdate(i, "start", e.target.value)}
              data-testid={`window-start-input-${i}`}
              className="h-11 px-3 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] font-mono focus:outline-none focus:border-terra-400/60"
            />
            <span className="text-slate-500">–</span>
            <input
              type="time"
              value={w.end}
              onChange={(e) => onUpdate(i, "end", e.target.value)}
              data-testid={`window-end-input-${i}`}
              className="h-11 px-3 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] font-mono focus:outline-none focus:border-terra-400/60"
            />
            <button
              onClick={() => setWindows(windows.filter((_, j) => j !== i))}
              data-testid={`delete-window-button-${i}`}
              className="p-2.5 rounded-xl text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors"
              title="Remove window"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
